/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit, inject, input, output, signal } from '@angular/core';
import { LucideChevronDown, LucideImage, LucideImageOff, LucideSearchX } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  UNSPLASH_IMAGES_DATA_SERVICE,
  UnsplashImagesDataService,
} from '../../../page-modules/admin/services/images/unsplash-images-data.service';
import { UnsplashPhoto } from '../../models/images/unsplash-images.model';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '../../services/toast-notifications/toast-service';
import { PtlBusyOverlayComponent } from '../../styleguide/components/busy-overlay/ptl-busy-overlay.component';
import { ButtonComponent } from '../../styleguide/components/buttons/default/button.component';
import { SearchComponent } from '../../styleguide/components/search/search.component';
import { V3EmptyStateComponent } from '../empty-state/v3-empty-state.component';

const SEARCH_DEBOUNCE_MS = 500;
// Must match the widest grid in the SCSS. A page from Unsplash is 10 photos, which is not a whole number
// of rows of 4, so the grid holds back a part-filled last row until the next page arrives.
const GRID_COLUMNS = 4;
const SKELETON_TILES = GRID_COLUMNS * 2;
// Covers and banners are wide, so portrait shots only get cropped down to a strip.
const PHOTO_ORIENTATION = 'landscape';

/**
 * The Unsplash tab of the V3 image dialog: a search bar and a grid of tiles. A tile carries only its
 * author credit — picking one downloads the photo and hands it straight to the crop step.
 */
@Component({
  selector: 'ptl-v3-unsplash-search',
  templateUrl: './v3-unsplash-search.component.html',
  styleUrls: ['./v3-unsplash-search.component.scss'],
  standalone: true,
  imports: [TranslocoModule, SearchComponent, ButtonComponent, PtlBusyOverlayComponent, V3EmptyStateComponent],
})
export class V3UnsplashSearchComponent implements OnInit, OnDestroy {
  initialPhrase = input('');

  photoPicked = output<File>();

  readonly skeletonTiles = Array.from({ length: SKELETON_TILES }, (_, index) => index);

  readonly LucideChevronDown = LucideChevronDown;
  readonly LucideImage = LucideImage;
  readonly LucideImageOff = LucideImageOff;
  readonly LucideSearchX = LucideSearchX;

  searchTerm = signal('');
  photos = signal<UnsplashPhoto[]>([]);
  isLoading = signal(false);
  isLoadingMore = signal(false);
  isDownloading = signal(false);
  hasError = signal(false);

  private pageNumber = 1;
  private totalPages = 1;
  private debounceTimer?: ReturnType<typeof setTimeout>;
  private lastSearchedTerm = '';
  private searchSubscription?: Subscription;
  private showMoreSubscription?: Subscription;

  private readonly http = inject(HttpClient);
  private readonly transloco = inject(TranslocoService);
  private readonly unsplashService = inject<UnsplashImagesDataService>(UNSPLASH_IMAGES_DATA_SERVICE);
  private readonly toastService = inject<ToastService>(TOAST_NOTIFICATION_SERVICE);

  ngOnInit(): void {
    this.runSearch();
  }

  ngOnDestroy(): void {
    clearTimeout(this.debounceTimer);
    this.cancelRequests();
  }

  get showNoResults(): boolean {
    return this.photos().length === 0 && this.activeTerm.length > 0;
  }

  get showStartPrompt(): boolean {
    return this.photos().length === 0 && this.activeTerm.length === 0;
  }

  /** Whole rows only while "Show more" can still fill the gap; the last page shows everything. */
  get visiblePhotos(): UnsplashPhoto[] {
    const photos = this.photos();
    if (!this.canShowMore) {
      return photos;
    }
    const wholeRowsCount = Math.floor(photos.length / GRID_COLUMNS) * GRID_COLUMNS;
    if (wholeRowsCount === 0) {
      return photos;
    }
    return photos.slice(0, wholeRowsCount);
  }

  get hasPhotos(): boolean {
    return this.photos().length > 0;
  }

  get canShowMore(): boolean {
    return this.photos().length > 0 && this.pageNumber < this.totalPages && !this.hasError();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);

    // A manual debounce rather than `debounceTime`: Firefox drops emissions on some input streams.
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.runSearch(), SEARCH_DEBOUNCE_MS);
  }

  authorLink(photo: UnsplashPhoto): string {
    const profileUrl = photo.user?.links?.html;
    if (!profileUrl) {
      return '';
    }
    return `${profileUrl}?utm_source=potentially&utm_medium=referral`;
  }

  authorName(photo: UnsplashPhoto): string {
    return photo.user?.name ?? '';
  }

  photoAlt(photo: UnsplashPhoto): string {
    return photo.description ?? '';
  }

  selectPhoto(photo: UnsplashPhoto): void {
    this.isDownloading.set(true);

    this.unsplashService
      .download(photo.links.download_location)
      .pipe(take(1))
      .subscribe((result) => {
        if (!result.isSuccess || !result.value?.url) {
          this.handleDownloadError();
          return;
        }
        this.downloadPhotoFile(photo, result.value.url);
      });
  }

  showMore(): void {
    if (this.isLoadingMore()) {
      return;
    }

    this.pageNumber += 1;
    this.isLoadingMore.set(true);
    this.hasError.set(false);

    this.showMoreSubscription = this.unsplashService
      .search(this.lastSearchedTerm, this.pageNumber, PHOTO_ORIENTATION)
      .pipe(take(1))
      .subscribe((response) => {
        this.isLoadingMore.set(false);

        if (!response.isSuccess) {
          this.pageNumber -= 1;
          this.toastService.showFail(this.transloco.translate('translations.errors.unableToLoadImage'));
          return;
        }

        const nextPhotos = response.value?.results ?? [];
        this.photos.set([...this.photos(), ...nextPhotos]);
      });
  }

  private runSearch(): void {
    // A slower, older response must not land on top of this one.
    this.cancelRequests();

    const term = this.activeTerm;
    this.lastSearchedTerm = term;
    if (term.length === 0) {
      this.photos.set([]);
      this.isLoading.set(false);
      return;
    }

    this.pageNumber = 1;
    this.isLoading.set(true);
    this.hasError.set(false);

    this.searchSubscription = this.unsplashService
      .search(term, this.pageNumber, PHOTO_ORIENTATION)
      .pipe(take(1))
      .subscribe((response) => {
        this.isLoading.set(false);

        if (!response.isSuccess) {
          this.photos.set([]);
          this.hasError.set(true);
          return;
        }

        this.photos.set(response.value?.results ?? []);
        this.totalPages = response.value?.total_pages ?? 1;
      });
  }

  private get activeTerm(): string {
    const typedTerm = this.searchTerm().trim();
    if (typedTerm.length > 0) {
      return typedTerm;
    }
    return this.initialPhrase().trim();
  }

  private cancelRequests(): void {
    this.searchSubscription?.unsubscribe();
    this.showMoreSubscription?.unsubscribe();
    this.isLoadingMore.set(false);
  }

  private downloadPhotoFile(photo: UnsplashPhoto, url: string): void {
    this.http.get(url, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        this.isDownloading.set(false);
        if (!blob) {
          this.handleDownloadError();
          return;
        }
        this.photoPicked.emit(this.toPhotoFile(photo, blob));
      },
      error: () => this.handleDownloadError(),
    });
  }

  private toPhotoFile(photo: UnsplashPhoto, blob: Blob): File {
    const extension = blob.type?.split('/')[1];
    let fileName = photo.links.download_location;
    if (extension) {
      fileName = `${fileName}.${extension}`;
    }
    return new File([blob], fileName, { type: blob.type });
  }

  private handleDownloadError(): void {
    this.isDownloading.set(false);
    this.toastService.showFail(this.transloco.translate('translations.uploadImageError'));
  }
}
